import type { Rgb } from 'cadenza/utils/colors/colors';
import { parseColor, rgbToHsl } from 'cadenza/utils/colors/colors';

/*
 * Our code depends on some theme properties being defined,
 * but the theme.js should be empty by default.
 * So we apply the defaults in here.
 */
const THEME_DEFAULTS = {
  'primary-color': '#2370b7',

  'main-header-invert': true,

  'login-invert': true,
  'login-aside': true,

  'quick-intro': true,

  'worksheet-bg': '#ffffff',
  'workbook-view-bg': '#ffffff'
};

const CUSTOMIZABLE_COLORS = [
  'default-color',
  'link-color',
  'primary-color'
];

const OTHER_CUSTOMIZABLE_PROPS = [
  'visited-link-color',
  'default-font-size',
  'default-font-family',
  'heading-font-family',
  'main-header-bg',
  'view-header-bg',
  'main-header-height',
  'login-bg',
  'login-header-height'
];

type Theme = Record<string, string>;

/**
 * Applies global properties of the Theming API.
 *
 * _Note:_ Use-case specific (non-global) properties are applied in related code.
 *
 * @param theme - The theme properties
 * @see {@link https://git.disy.net/cadenza/cadenza/-/blob/master/Cadenza_Web/src/main/asciidoc/web-theming.adoc#user-content-theming-api-using-theme-js|Theming API docs}
 */
export function applyThemeProperties (theme: Theme) {
  Object.assign(theme, THEME_DEFAULTS, { ...theme });
  CUSTOMIZABLE_COLORS.forEach(prop => applyColor(theme, prop));
  OTHER_CUSTOMIZABLE_PROPS.forEach(prop => applyProp(theme, prop));
}

function applyColor (theme: Theme, prop: string) {
  const customValue = theme[prop];
  if (customValue) {
    const rgb = parseColor(customValue).slice(0, 3) as Rgb;
    applyPropToDoc(`${prop}-rgb`, rgb.join());

    if (prop === 'primary-color') {
      const [ h, s, l ] = rgbToHsl(rgb);
      [ 15, 30 ].forEach(dl => applyPropToDoc(
        `${prop}-darken-${dl}`,
        `hsl(${h}deg, ${s * 100}%, ${l * (100 - dl)}%)`));
    }
  }
}

function applyProp (theme: Theme, prop: string) {
  const customValue = theme[prop];
  if (customValue) {
    applyPropToDoc(prop, customValue);
  }
}

function applyPropToDoc (prop: string, value: string) {
  document.documentElement.style.setProperty(`--${prop}`, value);
}
